import { useEffect, useMemo, useRef, useState } from 'react';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { listServiceCentersByCompanyId, updateServiceCenter } from '../../graphql/companyResourceQueries';
import type { ServiceCenter } from '../../types/schema';
import { filterVendorsByStatus, sortVendorsByName } from '../../utils/companyResourceUtils';
import { cleanIdList, nameMap, namesForIds } from '../../utils/serviceCenterUtils';
import { Badge, Btn, IconArchive, IconPlus, IconUsers } from '../ui';
import { ArchiveServiceCenterModal } from './ArchiveServiceCenterModal';
import { ServiceCenterModal } from './ServiceCenterModal';

interface ServiceCentersTabProps {
    isAdmin: boolean;
    companyId: string;
}

export const ServiceCentersTab = ({ isAdmin, companyId }: ServiceCentersTabProps) => {
    const { companyRegions, companyLocations } = useRegionAndLocationContext();
    const [serviceCenters, setServiceCenters] = useState<ServiceCenter[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingServiceCenter, setEditingServiceCenter] = useState<ServiceCenter | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [archivingServiceCenter, setArchivingServiceCenter] = useState<{ id: string; name?: string | null } | null>(null);
    const [isArchiving, setIsArchiving] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchServiceCenters = async () => {
        try {
            setIsLoading(true);
            const data = await listServiceCentersByCompanyId(companyId);
            if (!isMountedRef.current) return;
            setServiceCenters(data);
        } catch (err) {
            console.error('Error fetching service centers:', err);
        } finally {
            if (isMountedRef.current) setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!companyId) {
            setServiceCenters([]);
            setIsLoading(false);
            return;
        }
        let isCancelled = false;
        (async () => {
            try {
                setIsLoading(true);
                const data = await listServiceCentersByCompanyId(companyId);
                if (isCancelled) return;
                setServiceCenters(data);
            } catch (err) {
                console.error('Error fetching service centers:', err);
            } finally {
                if (!isCancelled) setIsLoading(false);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId]);

    const handleSave = async () => {
        setShowModal(false);
        setEditingServiceCenter(null);
        await fetchServiceCenters();
    };

    const handleConfirmArchive = async () => {
        if (!archivingServiceCenter) return;
        setIsArchiving(true);
        try {
            const current = serviceCenters.find((item) => item.id === archivingServiceCenter.id);
            await updateServiceCenter({
                id: archivingServiceCenter.id,
                companyId,
                name: current?.name || archivingServiceCenter.name || 'Service Center',
                archived: true,
                regionIds: cleanIdList(current?.regionIds),
                locationIds: cleanIdList(current?.locationIds),
            });
            if (!isMountedRef.current) return;
            setServiceCenters((prev) => prev.filter((item) => item.id !== archivingServiceCenter.id));
            setArchivingServiceCenter(null);
        } catch (err) {
            console.error('Error archiving service center:', err);
        } finally {
            if (isMountedRef.current) setIsArchiving(false);
        }
    };

    const regionNames = useMemo(() => nameMap(companyRegions), [companyRegions]);
    const locationNames = useMemo(() => nameMap(companyLocations), [companyLocations]);

    const activeServiceCenters = useMemo(
        () => sortVendorsByName(filterVendorsByStatus(serviceCenters, 'active')),
        [serviceCenters],
    );
    const displayedServiceCenters = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return activeServiceCenters;
        return activeServiceCenters.filter((serviceCenter) => (serviceCenter.name || '').toLowerCase().includes(query));
    }, [activeServiceCenters, search]);

    if (isLoading) {
        return <div className="cr-empty-state"><p className="cr-empty-state-text">Loading service centers...</p></div>;
    }

    return (
        <div>
            <div className="cr-toolbar">
                <input
                    id="cr-service-center-search"
                    className="cr-search-input"
                    type="search"
                    aria-label="Filter service centers by name"
                    placeholder="Filter service centers by name..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
                {isAdmin && (
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Service Center"
                        buttonOnClick={() => { setEditingServiceCenter(null); setShowModal(true); }}
                    />
                )}
            </div>

            {activeServiceCenters.length === 0 ? (
                <div className="cr-empty-state">
                    <div className="cr-empty-state-icon" aria-hidden="true">🏢</div>
                    <p className="cr-empty-state-text">No service centers yet. Add a new Service Center to get started.</p>
                </div>
            ) : displayedServiceCenters.length === 0 ? (
                <div className="cr-empty-state">
                    <p className="cr-empty-state-text">No service centers match your search.</p>
                </div>
            ) : (
                <div className="cr-vendor-grid">
                    {displayedServiceCenters.map((serviceCenter) => {
                        const regionLabel = namesForIds(cleanIdList(serviceCenter.regionIds), regionNames);
                        const locationLabel = namesForIds(cleanIdList(serviceCenter.locationIds), locationNames);
                        return (
                            <div key={serviceCenter.id} className="cr-vendor-card">
                                <div className="cr-vendor-card-header">
                                    <h3 className="cr-vendor-card-name">{serviceCenter.name}</h3>
                                </div>
                                {serviceCenter.address && (
                                    <div className="cr-vendor-card-row">
                                        <span className="cr-vendor-card-row-label">Address:</span>
                                        <span className="cr-vendor-card-row-value">{serviceCenter.address}</span>
                                    </div>
                                )}
                                {serviceCenter.phoneNumber && (
                                    <div className="cr-vendor-card-row">
                                        <span className="cr-vendor-card-row-label">Phone:</span>
                                        <span className="cr-vendor-card-row-value">{serviceCenter.phoneNumber}</span>
                                    </div>
                                )}
                                {serviceCenter.faxNumber && (
                                    <div className="cr-vendor-card-row">
                                        <span className="cr-vendor-card-row-label">Fax:</span>
                                        <span className="cr-vendor-card-row-value">{serviceCenter.faxNumber}</span>
                                    </div>
                                )}
                                {serviceCenter.repName && (
                                    <div className="cr-vendor-card-row">
                                        <Badge
                                            badgeClasses="badge-info"
                                            buttonSize="sm"
                                            badgeIcon={<IconUsers />}
                                            badgeText={serviceCenter.repName}
                                        />
                                        {serviceCenter.repPhoneNumber && (
                                            <span className="cr-vendor-card-row-value">{serviceCenter.repPhoneNumber}</span>
                                        )}
                                    </div>
                                )}
                                <div className="cr-vendor-card-row">
                                    <span className="cr-vendor-card-row-label">Regions:</span>
                                    <span className="cr-vendor-card-row-value">{regionLabel || 'All regions'}</span>
                                </div>
                                <div className="cr-vendor-card-row">
                                    <span className="cr-vendor-card-row-label">Locations:</span>
                                    <span className="cr-vendor-card-row-value">{locationLabel || 'All locations'}</span>
                                </div>
                                {isAdmin && (
                                    <div className="cr-vendor-card-actions">
                                        <Btn
                                            variant="secondary-outlined"
                                            buttonSize="sm"
                                            buttonText="Edit"
                                            buttonOnClick={() => { setEditingServiceCenter(serviceCenter); setShowModal(true); }}
                                        />
                                        <Btn
                                            variant="danger-outlined"
                                            buttonSize="sm"
                                            buttonIcon={<IconArchive />}
                                            buttonText="Archive"
                                            buttonOnClick={() => setArchivingServiceCenter(serviceCenter)}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <ServiceCenterModal
                    isOpen={showModal}
                    onClose={() => { setShowModal(false); setEditingServiceCenter(null); }}
                    onSave={handleSave}
                    editServiceCenter={editingServiceCenter}
                    companyId={companyId}
                />
            )}

            <ArchiveServiceCenterModal
                serviceCenter={archivingServiceCenter}
                isArchiving={isArchiving}
                onDismiss={() => {
                    if (!isArchiving) setArchivingServiceCenter(null);
                }}
                onConfirmArchive={handleConfirmArchive}
            />
        </div>
    );
};
